import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GhsClassification, MoleculeFacts, PublishedStatement } from '@peruselab/api-client/molecules';
import { Icon } from '../../../components/Icon';
import { ghsWording } from './factsheetText';
import { FactsSection } from './FactsSection';

const HAZARDS_SHOWN = 3;
const ECHA = 'European Chemicals Agency (ECHA)';

function Classification({ classification }: { readonly classification: GhsClassification }) {
  const { t, i18n } = useTranslation();
  const [all, setAll] = useState(false);
  const heading =
    classification.source === ECHA
      ? t('moleculePage.classificationEcha')
      : t('moleculePage.classificationFrom', { source: classification.source });
  const hazards = all ? classification.hazards : classification.hazards.slice(0, HAZARDS_SHOWN);
  const percent = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 });
  return (
    <div className="ghs-classification">
      <p className="ghs-classification-source">
        {classification.sourceUrl ? (
          <a href={classification.sourceUrl} target="_blank" rel="noopener noreferrer" className="button-with-icon">
            <span>{heading}</span>
            <Icon name="openInNew" size="0.85rem" />
          </a>
        ) : (
          heading
        )}
        {classification.reports ? (
          <span className="ghs-classification-reports">
            {' · '}
            {t('moleculePage.classificationReports', { count: classification.reports })}
          </span>
        ) : null}
      </p>
      {classification.pictograms.length > 0 || classification.signal ? (
        <div className="ghs-pictograms">
          {classification.pictograms.map((code) => (
            // Icon and word (icons-over-words, never colour alone); the drawing is the UN symbol.
            <figure key={code} className="ghs-pictogram">
              <img src={`${import.meta.env.BASE_URL}ghs/${code}.svg`} alt="" width={56} height={56} />
              <figcaption>{t(`ghs.pictogram.${code}`, { defaultValue: code })}</figcaption>
            </figure>
          ))}
          {classification.signal ? (
            <strong className={`ghs-signal ghs-signal--${classification.signal.toLowerCase()}`}>
              {t(`ghs.signal.${classification.signal}`, { defaultValue: classification.signal })}
            </strong>
          ) : null}
        </div>
      ) : null}
      {hazards.length > 0 ? (
        <ul className="ghs-hazards">
          {hazards.map((hazard) => {
            const wording = ghsWording(hazard.code, (part) => {
              const key = `ghs.hazard.${part}`;
              return i18n.exists(key) ? t(key) : undefined;
            });
            return (
              <li key={hazard.code}>
                <code dir="ltr">{hazard.code}</code>
                {wording ? <span>{wording}</span> : null}
                {hazard.percent !== null ? (
                  <span className="ghs-hazard-share">
                    {t('moleculePage.hazardShare', { percent: percent.format(hazard.percent) })}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {classification.hazards.length > HAZARDS_SHOWN ? (
        <button type="button" className="discussion-link-button" aria-expanded={all} onClick={() => setAll(!all)}>
          {all ? t('moleculePage.showFewer') : t('moleculePage.showAll', { count: classification.hazards.length })}
        </button>
      ) : null}
    </div>
  );
}

/**
 * ADR 0017 phase 3 (#908): Safety -- each classifier's GHS classification as facts, in our
 * pictograms and the standard UN GHS wording (owner, 2026-10-10: no source text), and the toxicity
 * summary from allowed sources. Always under the D7 label; absent when there's nothing to show.
 */
export function SafetyFacts({
  facts,
  toxicity: toxicityStatements
}: {
  readonly facts: MoleculeFacts;
  /** #947: the toxicity summaries as assertions, when imported; PubChem's cached text otherwise. */
  readonly toxicity?: readonly PublishedStatement[] | null;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const ghs = facts.ghs ?? [];
  const toxicity = toxicityStatements ?? facts.toxicity ?? [];
  if (ghs.length === 0 && toxicity.length === 0) return null;
  const sources = [
    ...new Set([...ghs.map((c) => (c.source === ECHA ? 'ECHA' : c.source)), ...toxicity.map((s) => s.source)])
  ];
  return (
    <section className="published-facts-section safety-facts" aria-labelledby={headingId}>
      <h2 id={headingId}>{t('moleculePage.safety')}</h2>
      {/* D7: what this is and isn't, always first. */}
      <p className="safety-label" role="note">
        <Icon name="info" size="1rem" />
        <span>{t('moleculePage.safetyLabel', { sources: sources.join(', ') })}</span>
      </p>
      {ghs.map((classification) => (
        <Classification
          key={`${classification.source}-${classification.reports ?? ''}`}
          classification={classification}
        />
      ))}
      <FactsSection title={t('moleculePage.toxicity')} statements={toxicity} shownFirst={1} nested />
    </section>
  );
}
